/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WithdrawalReasons, WithdrawRequest, Customer, PortfolioAllocationV2, PortfolioAllocationResponse, ApiAccount } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as fromAppRoot from '@app/app.reducers';

import { PromoCampaign, PromoParticipation } from '@shared/models/DigitalPromo';

import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  accountID: string;

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.store.pipe(select(fromAppRoot.selectCurrentAccount)).subscribe((value) => {
      this.accountID = value.id;
    });
  }

  fetchCustomer(): Observable<Customer> {
    return this.http.get<Customer>(`${environment.apiUrl}`);
  }

  resendVerificationEmail(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/auth/confirmation`, {});
  }

  fetchCustomerAccountDetail(accountID: string): Observable<ApiAccount> {
    return this.http.get<ApiAccount>(`${environment.apiUrl}/accounts/${accountID}`);
  }

  createAccount(payload: { kind: string; owner_name?: string }): Observable<ApiAccount> {
    return this.http.post<ApiAccount>(`${environment.apiUrl}/accounts`, { account: { ...payload } });
  }

  createSupportTicket(message: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/support`, { body: { message } });
  }

  updateSavingsPlan(value: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/accounts/${this.accountID}/strategy/savings-plan`, {
      period_investment: value
    });
  }

  requestReInvestment() {
    return this.http.post(`${environment.apiUrlV3}/reinvestment_requests`, { data: { attributes: { account_id: this.accountID } } });
  }

  getPromoCampaigns(): Observable<PromoCampaign> {
    return this.http.get<PromoCampaign>(`${environment.apiUrlV3}/promotions/promotion_campaigns?include=deposit_promotions`);
  }

  setPromoParticipation(participation: boolean, id: number): Observable<{ data: PromoParticipation }> {
    const payload = {
      data: {
        type: 'promotion_campaign_customers',
        attributes: {
          opted_in: participation,
          promotion_campaign_id: id
        }
      }
    };

    return this.http.put<{ data: PromoParticipation }>(`${environment.apiUrlV3}/promotions/promotion_campaign_customers`, payload);
  }

  submitGreennessSurvey(data: { greenness_score?: number; net_worth?: string; annual_income?: string }): Observable<any> {
    return this.http.post<any>(`${environment.apiUrlV4}/tracking/greenness_survey_submission`, data);
  }

  // Withdrawals 2.0

  getPortfolioAllocation(): Observable<PortfolioAllocationResponse> {
    return this.http.get<PortfolioAllocationResponse>(`${environment.apiUrlV3}/portfolio_allocations/${this.accountID}`);
  }

  getPortfolioAllocationV2(): Observable<PortfolioAllocationV2> {
    return this.http
      .get<{ portfolio_allocation: PortfolioAllocationV2 }>(`${environment.apiUrlV4}/reporting/accounts/${this.accountID}/portfolio_allocation`)
      .pipe(map((res) => res.portfolio_allocation));
  }

  checkWithdrawalInProgress(): Observable<{ data: WithdrawRequest[] }> {
    return this.http.get<{ data: WithdrawRequest[] }>(
      `${environment.apiUrlV3}/new_funds_withdrawal_requests?filter[status]=in_progress&filter[account_id]=${this.accountID}`
    );
  }

  getWithdrawalReasonOptions(): Observable<{ id: number; name: string; survey_questions: WithdrawalReasons[] }> {
    return this.http.get<{ id: number; name: string; survey_questions: WithdrawalReasons[] }>(
      `${environment.apiUrl}/accounts/${this.accountID}/survey/withdrawals_feedback`
    );
  }
}
